import type { ReactElement, ReactNode } from "react"
import Link from "@onestore/hel/dist/components/quarks/Link"
import type { CtaElement } from "@gatsby-plugin-generic-page/fragments/ctaElement"
import getBackground from "@gatsby-plugin-generic-page/helpers/getBackground"
import { getCtaButtonFlatData } from "@gatsby-plugin-generic-page/helpers/getCtaElementFlatData"
import { getStaticPrices } from "@gatsby-plugin-generic-page/helpers/staticPrices"
import useProductBoxTitleWithLabels from "@gatsby-plugin-generic-page/hooks/useProductBoxTitleWithLabels"
import { CardTariff } from "@ionos-web-design-system/react"
import { usePriceTypeContext } from "~/context/PriceTypeContext"
import type { StaticPrice } from "~/fragments/staticPrice"
import { sendGaEvent } from "~/lib/analytics"
import isEmpty from "~/lib/isEmpty"
import { getDetailedStaticPriceData } from "../DetailedStaticPrice/helpers/getDetailedStaticPriceData"
import type { ProductType } from "."
import ProductBoxButton from "./ProductBoxButton"
import ProductBoxSlimFooter from "./ProductBoxFooter"
import TrialProductBox from "./TrialProductBox"

export type ProductBoxWithCtaProps = {
  product: ProductType
  cta: CtaElement[]
  staticPrices?: StaticPrice[]
  isTrialBox?: boolean
  isDisabled?: boolean
}

export default function ProductBoxWithCta({
  product,
  cta,
  staticPrices,
  isTrialBox,
  isDisabled,
}: ProductBoxWithCtaProps): ReactElement<ProductBoxWithCtaProps> | null {
  const { currentPriceType } = usePriceTypeContext()

  const prices = staticPrices
    ? getStaticPrices(staticPrices, currentPriceType)
    : undefined

  const titleWithLabels = useProductBoxTitleWithLabels(
    product.title,
    product.discountLabel,
    product.labels
  )

  if (isEmpty(cta)) {
    return null
  }

  const { text, title, gaEvent, href, openInNewTab, pages } =
    getCtaButtonFlatData(cta[0], currentPriceType)

  const period =
    prices && Object.keys(prices).length === 1
      ? Object.keys(prices)[0]
      : product.period || (prices && Object.keys(prices)[0])

  const { customOnColorText, imageBackground } = getBackground(
    product.backgroundImage
  )

  const isTrialBoxVariant =
    isTrialBox && !isEmpty(imageBackground) && !isEmpty(customOnColorText)

  const trialButtonDataTestId = "product_box_select_button"

  const staticPriceResult =
    !isEmpty(staticPrices) && !isEmpty(period)
      ? getDetailedStaticPriceData(
          staticPrices,
          period,
          currentPriceType,
          product.additionalText,
          undefined,
          true
        )
      : null

  const buttonNode: ReactNode = !isDisabled ? (
    <Link
      href={href}
      to={!isEmpty(pages) ? pages[0].flatData.url : undefined}
      title={title}
      openInNewTab={openInNewTab}
      onClick={() => sendGaEvent(gaEvent)}
      className="contents"
    >
      <ProductBoxButton
        concept={product.concept}
        variant={product.variant}
        dataTestId={isTrialBoxVariant ? trialButtonDataTestId : undefined}
      >
        {product.buttonText || text}
      </ProductBoxButton>
    </Link>
  ) : (
    <ProductBoxButton
      concept={product.concept}
      variant={product.variant}
      disabled
      dataTestId={isTrialBoxVariant ? trialButtonDataTestId : undefined}
    >
      {product.buttonText || text}
    </ProductBoxButton>
  )

  const cardTariff = (
    <CardTariff
      title={titleWithLabels}
      subtitle={product.description}
      badgeText={product.promoText}
      priceData={
        !isEmpty(staticPriceResult) && !isEmpty(staticPriceResult.data)
          ? { ...staticPriceResult.data, reservePrelineSpace: true }
          : undefined
      }
      button={buttonNode}
      className="leading-normal"
    >
      <div className="flex flex-col gap-gap-8 h-full">
        {product.content.parameters}

        {!isEmpty(product.content?.footer) ? (
          <ProductBoxSlimFooter
            {...product.content.footer}
            setIsChecked={() => {}}
            isChecked={false}
          />
        ) : null}
      </div>
    </CardTariff>
  )

  if (isTrialBoxVariant) {
    return (
      <TrialProductBox
        title={titleWithLabels}
        subtitle={product.description}
        badgeText={product.promoText}
        button={buttonNode}
        imageBackground={imageBackground}
        customOnColorText={customOnColorText}
      >
        {product.content.parameters}
      </TrialProductBox>
    )
  }

  return cardTariff
}
